"use client";

import { ArrowUp, Check, Leaf, Loader2, Sparkles } from "lucide-react";

import { CREATE_TOPIC, PROMPT_CHARS, SAMPLE_TOPIC } from "./loop-constants";
import { StageCard } from "./StageCard";

/**
 * Stage one of the loop: a topic being *created*, not chosen.
 *
 * This is the product's real first move — you describe what you want to
 * practise and the AI writes the lesson — so the beat shows it end to
 * end: the prompt types itself, the send button fires, three skeleton
 * cards stand in while the AI writes, and the generated topic resolves
 * out of focus with its question and word counts.
 *
 * It is a single CSS-timed sequence rather than a state machine. The
 * stage re-mounts on every pass (AnimatePresence keys on the stage id),
 * so the keyframes restart in sync with the loop and nothing has to be
 * torn down between beats. Every offset is a constant below, so the
 * whole choreography can be read in one place and tuned as one thing.
 *
 * Base styles are the *finished* frame, and layers that only exist part
 * way through (the prompt, the invitation, the skeletons, "writing…")
 * are hidden at rest. So with motion off, what is left on screen is the
 * end of the story: an input back at its placeholder and a finished
 * topic. Nothing is left frozen mid-gesture.
 */

/** Seconds, from the moment the stage mounts. */
const T = {
  prompt: 0.3,
  typing: 0.75,
  send: 2.7,
  clear: 2.85,
  skeleton: 2.95,
  resolve: 4.15,
  counts: 4.8,
  question: 5.25,
  ready: 5.85,
};

const ms = (seconds: number) => `${Math.round(seconds * 1000)}ms`;

/** Three placeholders, each appearing a beat after the last — the same
 *  rhythm as the grid skeletons the app shows while a topic generates. */
const SKELETONS = [0, 0.18, 0.36];

export function TopicCreateDemo() {
  return (
    <StageCard className="flex flex-col">
      {/* ── The prompt ───────────────────────────────────── */}
      <div
        className="flex items-center justify-between gap-3"
        style={{ animation: `loop-reveal 0.5s ease both ${ms(T.prompt)}` }}
      >
        <p className="text-[12px] font-semibold text-white/80">
          {CREATE_TOPIC.heading}
        </p>
        <Sparkles
          className="h-3.5 w-3.5 shrink-0 text-secondary"
          aria-hidden="true"
        />
      </div>

      <div
        className="relative mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5"
        style={{
          animation: `loop-reveal 0.5s ease both ${ms(T.prompt + 0.1)}`,
        }}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white/10">
          <Loader2
            className="loop-spin h-3 w-3 text-white/60"
            aria-hidden="true"
          />
        </span>

        {/* The prompt clears out of the input once it has been sent — the
            wrapper fades, the typing underneath it keeps its own clock. */}
        <span className="relative min-w-0 grow overflow-hidden">
          {/* Once sent, the input falls back to its placeholder, which is
              what the real one does — and beats an abandoned empty box. */}
          <span
            className="loop-reveal absolute inset-0 flex items-center text-[12px] leading-none text-white/25"
            style={{ animationDelay: ms(T.clear + 0.2) }}
          >
            {CREATE_TOPIC.placeholder}
          </span>
          <span
            data-loop="prompt"
            className="loop-clear block overflow-hidden opacity-0"
            style={{ animationDelay: ms(T.clear) }}
          >
            <span
              className="loop-type relative inline-flex items-center"
              style={
                {
                  "--type-steps": PROMPT_CHARS,
                  "--type-duration": "1.8s",
                  animationDelay: ms(T.typing),
                } as React.CSSProperties
              }
            >
              <span className="whitespace-nowrap text-[12px] leading-none text-white/85">
                {CREATE_TOPIC.prompt}
              </span>
              {/* The caret sits at the end of the reveal, so it tracks
                  the last character typed rather than blinking at the start. */}
              <span
                aria-hidden="true"
                className="loop-caret ml-px inline-block h-[11px] w-px shrink-0 bg-secondary opacity-0"
              />
            </span>
          </span>
        </span>

        <span
          className="loop-send flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-white"
          style={{ animationDelay: ms(T.send) }}
        >
          <ArrowUp className="h-3 w-3" aria-hidden="true" />
        </span>
      </div>

      {/* Generation bar — a progress fill, not an endless shimmer: the
          shimmer is the skeletons' job. A bar that keeps moving after the
          work is done is lying about the state. */}
      <div className="relative mt-3 flex h-4 items-center gap-2.5">
        <span className="relative h-[3px] grow overflow-hidden rounded-full bg-white/10">
          <span
            className="loop-fill absolute inset-0 rounded-full bg-gradient-to-r from-primary to-secondary"
            style={
              {
                "--loop-dwell": `${Math.round((T.resolve - T.skeleton) * 1000)}ms`,
                animationDelay: ms(T.skeleton),
              } as React.CSSProperties
            }
          />
        </span>

        <span className="relative h-4 w-[150px] shrink-0 sm:w-[190px]">
          <span
            className="loop-clear absolute inset-0 flex items-center gap-1.5 text-[10px] text-white/45 opacity-0"
            style={{ animationDelay: ms(T.resolve) }}
          >
            <Sparkles
              className="loop-spin h-2.5 w-2.5 shrink-0 text-secondary"
              aria-hidden="true"
            />
            {CREATE_TOPIC.status}
          </span>
          <span
            className="loop-reveal absolute inset-0 flex items-center gap-1.5 text-[10px] text-emerald-300/80"
            style={{ animationDelay: ms(T.resolve + 0.25) }}
          >
            <Check className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
            {CREATE_TOPIC.done}
          </span>
        </span>
      </div>

      {/* ── Result slot ────────────────────────────────────
          Three states share one slot and crossfade in place: the
          invitation while you type, the skeletons while the AI
          writes, then the finished topic. Nothing reflows. */}
      <div className="relative mt-3 grow">
        {/* Before it is sent — the slot is never simply empty */}
        <div
          aria-hidden="true"
          data-loop="invite"
          className="loop-fade absolute inset-0 flex items-center justify-center gap-2 text-center text-[11px] leading-snug text-white/35 opacity-0"
          style={{ animationDelay: ms(T.clear) }}
        >
          <span className="flex shrink-0 gap-1">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className="loop-typing h-1 w-1 rounded-full bg-white/40"
                style={{ animationDelay: `${0.2 + dot * 0.16}s` }}
              />
            ))}
          </span>
          Describe a topic — the questions get written for you
        </div>

        {/* While it is being written */}
        <div
          aria-hidden="true"
          data-loop="skeletons"
          className="loop-fade absolute inset-0 opacity-0"
          style={{ animationDelay: ms(T.resolve) }}
        >
          <div className="flex h-full gap-2">
            {SKELETONS.map((delay, i) => (
              <div
                key={i}
                className="loop-reveal flex-1 rounded-xl border border-white/10 bg-white/[0.03] p-3"
                style={{ animationDelay: ms(T.skeleton + delay) }}
              >
                <span className="loop-skeleton block h-7 w-7 rounded-lg" />
                <span className="loop-skeleton mt-2.5 block h-2.5 w-4/5 rounded-full" />
                <span className="loop-skeleton mt-2 block h-2.5 w-3/5 rounded-full" />
                <span className="loop-skeleton mt-3 block h-2 w-2/5 rounded-full" />
              </div>
            ))}
          </div>
        </div>

        {/* Once it is written */}
        <div
          data-loop="result"
          className="loop-reveal absolute inset-0"
          style={{ animationDelay: ms(T.resolve) }}
        >
          <div className="flex items-center gap-2.5 rounded-xl border border-primary/20 bg-primary/[0.07] px-3.5 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-400/15 text-emerald-300">
              <Leaf className="h-4 w-4" aria-hidden="true" />
            </span>

            <div className="min-w-0 grow">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">
                {SAMPLE_TOPIC.category} · {SAMPLE_TOPIC.level}
              </p>
              <p className="mt-0.5 font-display text-[15px] font-semibold leading-tight text-white">
                {SAMPLE_TOPIC.title}
              </p>

              <div
                className="loop-reveal mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-white/50"
                style={{ animationDelay: ms(T.counts) }}
              >
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Check
                    className="h-3 w-3 text-emerald-300/80"
                    aria-hidden="true"
                  />
                  {CREATE_TOPIC.questionCount} questions
                </span>
                <span className="h-2.5 w-px bg-white/15" aria-hidden="true" />
                <span className="whitespace-nowrap">
                  {CREATE_TOPIC.wordCount} words saved
                </span>
                <span
                  className="loop-reveal shrink-0 rounded-full bg-primary/20 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-blue-200"
                  style={{ animationDelay: ms(T.ready) }}
                >
                  Ready
                </span>
              </div>
            </div>
          </div>

          {/* The first question it wrote — the next beat of the loop, held
              on screen a moment so the sequence reads as continuous. */}
          <p
            className="loop-reveal mt-2 line-clamp-2 text-[11px] italic leading-snug text-white/40"
            style={{ animationDelay: ms(T.question) }}
          >
            “{CREATE_TOPIC.firstQuestion}”
          </p>
        </div>
      </div>
    </StageCard>
  );
}
